/**
 * Timezone utilities built on Intl (no Date.parse, no implicit local timezone).
 *
 * All instants are UTC epoch milliseconds. "Local" means wall-clock time in an
 * explicit IANA timezone. DST gaps (non-existent local times) and overlaps
 * (ambiguous local times) are surfaced explicitly to callers.
 */

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock parts of an instant in a timezone. */
export function localParts(utcMs: number, tz: string): LocalParts {
  const out: Record<string, number> = {};
  for (const p of partsFormatter(tz).formatToParts(new Date(utcMs))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    hour: out.hour === 24 ? 0 : out.hour,
    minute: out.minute,
    second: out.second,
  };
}

/** Offset of tz from UTC at the given instant, in minutes (e.g. +600 for AEST). */
export function tzOffsetMinutes(utcMs: number, tz: string): number {
  const p = localParts(utcMs, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const truncated = Math.floor(utcMs / 1000) * 1000;
  return Math.round((asUtc - truncated) / MINUTE);
}

function sameWallClock(a: LocalParts, b: Omit<LocalParts, 'second'> & { second?: number }) {
  return (
    a.year === b.year &&
    a.month === b.month &&
    a.day === b.day &&
    a.hour === b.hour &&
    a.minute === b.minute &&
    a.second === (b.second ?? 0)
  );
}

/**
 * Every UTC instant whose wall-clock time in `tz` equals the given parts.
 * - length 0: the local time does not exist (spring-forward gap)
 * - length 1: unambiguous
 * - length 2: ambiguous (fall-back overlap), sorted earliest first
 */
export function zonedToUtcCandidates(
  parts: Omit<LocalParts, 'second'> & { second?: number },
  tz: string,
): number[] {
  const naive = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second ?? 0);
  const offsets = new Set<number>();
  for (const probe of [naive - DAY, naive - 6 * HOUR, naive, naive + 6 * HOUR, naive + DAY]) {
    offsets.add(tzOffsetMinutes(probe, tz));
  }
  const results = new Set<number>();
  for (const off of offsets) {
    const candidate = naive - off * MINUTE;
    if (sameWallClock(localParts(candidate, tz), parts)) results.add(candidate);
  }
  return [...results].sort((a, b) => a - b);
}

export type ZonedResolution =
  | { kind: 'ok'; utcMs: number }
  | { kind: 'ambiguous'; candidates: [number, number] }
  | { kind: 'nonexistent' };

export function resolveZoned(parts: Omit<LocalParts, 'second'> & { second?: number }, tz: string): ZonedResolution {
  const c = zonedToUtcCandidates(parts, tz);
  if (c.length === 0) return { kind: 'nonexistent' };
  if (c.length === 1) return { kind: 'ok', utcMs: c[0] };
  return { kind: 'ambiguous', candidates: [c[0], c[1]] };
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** Local calendar day key "YYYY-MM-DD" for an instant. */
export function localDayKey(utcMs: number, tz: string): string {
  const p = localParts(utcMs, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

export function parseDayKey(key: string): { year: number; month: number; day: number } {
  const [y, m, d] = key.split('-').map(Number);
  return { year: y, month: m, day: d };
}

/** Pure calendar arithmetic on day keys (DST independent). */
export function addDaysToKey(key: string, n: number): string {
  const { year, month, day } = parseDayKey(key);
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * UTC instant at which the given local day begins. Handles zones where
 * midnight is skipped by DST by taking the first existing minute of the day.
 */
export function startOfLocalDayFromKey(key: string, tz: string): number {
  const { year, month, day } = parseDayKey(key);
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const c = zonedToUtcCandidates({ year, month, day, hour: Math.floor(minutes / 60), minute: minutes % 60 }, tz);
    if (c.length) return c[0];
  }
  throw new Error(`Cannot resolve start of day ${key} in ${tz}`);
}

export function startOfLocalDay(utcMs: number, tz: string): number {
  return startOfLocalDayFromKey(localDayKey(utcMs, tz), tz);
}

/** Real elapsed length of a local day in ms (23h/24h/25h around DST). */
export function localDayLengthMs(key: string, tz: string): number {
  return startOfLocalDayFromKey(addDaysToKey(key, 1), tz) - startOfLocalDayFromKey(key, tz);
}

/**
 * Split [startMs, endMs) into per-local-day portions. Used for daily sleep
 * totals so that overnight sessions are attributed to each day correctly.
 */
export function splitByLocalDay(startMs: number, endMs: number, tz: string): Array<{ day: string; ms: number }> {
  if (!(endMs > startMs)) return [];
  const out: Array<{ day: string; ms: number }> = [];
  let cursor = startMs;
  let key = localDayKey(startMs, tz);
  while (cursor < endMs) {
    const nextKey = addDaysToKey(key, 1);
    const boundary = startOfLocalDayFromKey(nextKey, tz);
    const segEnd = Math.min(boundary, endMs);
    out.push({ day: key, ms: segEnd - cursor });
    cursor = segEnd;
    key = nextKey;
  }
  return out;
}

/** Value for <input type="datetime-local"> representing the instant in tz. */
export function toLocalInputValue(utcMs: number, tz: string): string {
  const p = localParts(utcMs, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function parseLocalInputValue(value: string, tz: string): ZonedResolution | { kind: 'invalid' } {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) return { kind: 'invalid' };
  return resolveZoned(
    { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5], second: m[6] ? +m[6] : 0 },
    tz,
  );
}

const displayCache = new Map<string, Intl.DateTimeFormat>();
function displayFormatter(tz: string, key: string, opts: Intl.DateTimeFormatOptions) {
  const k = `${tz}|${key}`;
  let f = displayCache.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat(undefined, { timeZone: tz, ...opts });
    displayCache.set(k, f);
  }
  return f;
}

export function formatTime(utcMs: number, tz: string): string {
  return displayFormatter(tz, 'time', { hour: 'numeric', minute: '2-digit' }).format(utcMs);
}

export function formatDateTime(utcMs: number, tz: string): string {
  return displayFormatter(tz, 'dt', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(utcMs);
}

export function formatDayHeading(key: string, tz: string, todayKey?: string): string {
  if (todayKey) {
    if (key === todayKey) return 'Today';
    if (key === addDaysToKey(todayKey, -1)) return 'Yesterday';
  }
  // Midday avoids DST edge rendering issues
  const { year, month, day } = parseDayKey(key);
  const c = zonedToUtcCandidates({ year, month, day, hour: 12, minute: 0 }, tz);
  return displayFormatter(tz, 'day', { weekday: 'long', day: 'numeric', month: 'long' }).format(c[0]);
}

export function formatShortDay(key: string): string {
  const { month, day } = parseDayKey(key);
  return `${day}/${month}`;
}

/** Abbreviated tz name at an instant, e.g. "AEDT". Falls back to GMT offset. */
export function tzAbbrev(utcMs: number, tz: string): string {
  const p = displayFormatter(tz, 'tzname', { timeZoneName: 'short' })
    .formatToParts(utcMs)
    .find((x) => x.type === 'timeZoneName');
  return p?.value ?? tz;
}

export function formatDuration(ms: number, opts: { seconds?: boolean } = {}): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (opts.seconds) return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  if (h === 0) return `${m}m`;
  return `${h}h ${pad(m)}m`;
}

export function formatRelative(ms: number): string {
  return `${formatDuration(ms)} ago`;
}

export const TIME = { MINUTE, HOUR, DAY };
